import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Header from "@/components/feature/Header";
import { SHOW_REVIEWS } from "@/config/features";
import Footer from "@/components/feature/Footer";
import { PHONE } from "@/mocks/updiag";

const certifications = [
  {
    name: "COFRAC",
    fullName: "Comité Français d'Accréditation",
    icon: "ri-award-fill",
    color: "text-[#F97316]",
    bg: "bg-orange-50",
    border: "border-orange-100",
    desc: "Accréditation officielle garantissant la compétence et l'impartialité de nos diagnostiqueurs selon la norme ISO/CEI 17024.",
  },
  {
    name: "Bureau Veritas",
    fullName: "Organisme de certification",
    icon: "ri-verified-badge-fill",
    color: "text-[#0F3B5F]",
    bg: "bg-blue-50",
    border: "border-blue-100",
    desc: "Partenaire certifié Bureau Veritas pour les diagnostics amiante, plomb et électricité. Contrôles qualité réguliers.",
  },
  {
    name: "RC Pro",
    fullName: "Responsabilité Civile Professionnelle",
    icon: "ri-shield-check-fill",
    color: "text-[#16A34A]",
    bg: "bg-green-50",
    border: "border-green-100",
    desc: "Assurance RC Pro couvrant l'ensemble de nos interventions. Vos intérêts sont protégés en cas d'erreur ou d'omission.",
  },
  {
    name: "Ministère du Logement",
    fullName: "Agrément officiel",
    icon: "ri-government-fill",
    color: "text-[#0F3B5F]",
    bg: "bg-blue-50",
    border: "border-blue-100",
    desc: "Agréé par le Ministère du Logement pour la réalisation des diagnostics immobiliers réglementaires en France.",
  },
];

const team = [
  {
    name: "Marc Lefebvre",
    role: "Fondateur & Diagnostiqueur certifié",
    exp: "15 ans d'expérience",
    certs: ["DPE", "Amiante", "Plomb", "Électricité", "Gaz"],
    img: "/img/readdy/team1.jpg",
  },
  {
    name: "Sophie Renard",
    role: "Diagnostiqueure certifiée",
    exp: "8 ans d'expérience",
    certs: ["DPE", "Amiante", "Loi Carrez", "Termites"],
    img: "/img/readdy/team2.jpg",
  },
  {
    name: "Antoine Moreau",
    role: "Diagnostiqueur certifié",
    exp: "6 ans d'expérience",
    certs: ["DPE", "Plomb", "Électricité", "Gaz"],
    img: "/img/readdy/team3.jpg",
  },
];

const values = [
  { icon: "ri-award-fill", title: "Excellence", desc: "Chaque diagnostic est réalisé avec rigueur et précision. Nos rapports sont clairs, complets et reconnus par tous les notaires.", color: "text-[#F97316]" },
  { icon: "ri-timer-flash-fill", title: "Réactivité", desc: "RDV sous 48h dans toute l'IDF, rapport le jour même. Nous respectons vos délais de vente ou de location.", color: "text-[#0F3B5F]" },
  { icon: "ri-hand-heart-fill", title: "Transparence", desc: "Tarifs affichés sans surprise, explications claires sur les résultats. Nous prenons le temps de vous expliquer chaque diagnostic.", color: "text-[#16A34A]" },
  { icon: "ri-map-pin-2-fill", title: "Proximité", desc: "Basés en Île-de-France, nous connaissons parfaitement le parc immobilier local et ses spécificités réglementaires.", color: "text-[#F97316]" },
];

export default function AProposPage() {
  return (
    <>
      <title>À Propos d'Up Diag — Expert Diagnostic Immobilier Paris & IDF depuis 10 ans</title>
      <meta name="description" content="Up Diag, expert en diagnostic immobilier à Paris et en Île-de-France depuis 10 ans. Certifié COFRAC, équipe de diagnostiqueurs qualifiés, +2500 diagnostics réalisés." />
      <TopBar />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/img/readdy/about-hero.jpg"
              alt="Équipe Up Diag"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-[#0F3B5F]/80"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/15 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              À propos
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5" style={{ fontFamily: "Barlow, sans-serif" }}>
              L'expert de confiance pour vos<br />
              <span className="text-[#F97316]">diagnostics immobiliers en IDF</span>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Depuis 10 ans, Up Diag accompagne les propriétaires, bailleurs et agences immobilières de Paris et d'Île-de-France avec des diagnostics fiables, rapides et certifiés.
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-white py-14 border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: "+2 500", label: "Diagnostics réalisés", icon: "ri-bar-chart-fill" },
                { value: "10 ans", label: "D'expertise en IDF", icon: "ri-history-fill" },
                ...(SHOW_REVIEWS
                  ? [{ value: "4.9/5", label: "Note Google (127 avis)", icon: "ri-star-fill" }]
                  : [{ value: "Cofrac", label: "Diagnostiqueurs certifiés", icon: "ri-award-fill" }]),
                { value: "48h", label: "Délai d'intervention max", icon: "ri-timer-flash-fill" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-xl mx-auto mb-3">
                    <i className={`${stat.icon} text-[#F97316] text-2xl`}></i>
                  </div>
                  <div className="text-3xl font-extrabold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Notre histoire */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-orange-100 text-[#F97316] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                  Notre histoire
                </span>
                <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-5" style={{ fontFamily: "Barlow, sans-serif" }}>
                  Né d'un constat simple : le diagnostic immobilier mérite mieux
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Fondée en 2015 par Marc Lefebvre, diagnostiqueur certifié avec 15 ans d'expérience, Up Diag est née d'une conviction : les propriétaires parisiens méritent un service de diagnostic immobilier à la hauteur de leurs exigences.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Face à un marché fragmenté, des délais trop longs et des tarifs opaques, Up Diag a créé le concept de <strong>guichet unique</strong> : DPE réalisé en propre, autres diagnostics via un réseau de partenaires certifiés COFRAC, le tout coordonné en un seul rendez-vous.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Aujourd'hui, notre équipe de diagnostiqueurs certifiés intervient dans les 8 départements d'Île-de-France, avec plus de 2 500 diagnostics réalisés et une expertise reconnue auprès des notaires et agences immobilières.
                </p>
              </div>
              <div className="relative">
                <img
                  src="/img/readdy/about-story.jpg"
                  alt="Marc Lefebvre, fondateur Up Diag"
                  className="w-full h-80 object-cover object-top rounded-2xl"
                  loading="lazy"
                />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-xl">
                      <i className="ri-award-fill text-[#F97316] text-xl"></i>
                    </div>
                    <div>
                      <div className="font-bold text-[#0F3B5F] text-sm">Certifié COFRAC</div>
                      <div className="text-xs text-gray-400">Depuis 2015</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Valeurs */}
        <section className="bg-white py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Nos valeurs</span>
              <h2 className="text-3xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>Ce qui nous distingue</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {values.map((v) => (
                <div key={v.title} className="flex items-start gap-5 bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-gray-100 flex-shrink-0">
                    <i className={`${v.icon} ${v.color} text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F3B5F] mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>{v.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-orange-100 text-[#F97316] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Certifications</span>
              <h2 className="text-3xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>Nos accréditations & certifications</h2>
              <p className="text-gray-500 mt-3 max-w-xl mx-auto">Toutes nos certifications sont à jour et vérifiables. Elles garantissent la qualité et la fiabilité de nos diagnostics.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {certifications.map((cert) => (
                <div key={cert.name} className={`${cert.bg} rounded-2xl p-6 border ${cert.border}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-white/80 flex-shrink-0">
                      <i className={`${cert.icon} ${cert.color} text-2xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F3B5F] mb-0.5" style={{ fontFamily: "Barlow, sans-serif" }}>{cert.name}</h3>
                      <p className="text-xs text-gray-500 mb-2">{cert.fullName}</p>
                      <p className="text-sm text-gray-600 leading-relaxed">{cert.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Équipe */}
        <section className="bg-white py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-blue-50 text-[#0F3B5F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Notre équipe</span>
              <h2 className="text-3xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>Des experts certifiés à votre service</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-7">
              {team.map((member) => (
                <div key={member.name} className="bg-[#F8FAFC] rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300">
                  <div className="h-56 overflow-hidden">
                    <img src={member.img} alt={member.name} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-[#0F3B5F] mb-0.5" style={{ fontFamily: "Barlow, sans-serif" }}>{member.name}</h3>
                    <p className="text-sm text-[#F97316] font-semibold mb-1">{member.role}</p>
                    <p className="text-xs text-gray-400 mb-4">{member.exp}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {member.certs.map((c) => (
                        <span key={c} className="bg-white border border-gray-200 text-[#0F3B5F] text-xs font-semibold px-2.5 py-1 rounded-full">{c}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0F3B5F] py-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Faites confiance à nos experts
            </h2>
            <p className="text-white/75 mb-8">Devis gratuit en 2 minutes — Intervention sous 48h dans toute l'IDF</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis" className="flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                <i className="ri-file-list-3-line"></i> Obtenir mon devis gratuit
              </Link>
              <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-phone-fill"></i> {PHONE}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
